const OPERATION_ID_PREFIX = 'zc';

let ownerId = null;
let runOperationId = null;
const completionOperationIds = new Map();
const missionEventIds = new Map();

function secureBytes(size) {
  if (typeof window === 'undefined' || !window.crypto?.getRandomValues) {
    throw new Error('secure_random_unavailable');
  }
  const bytes = new Uint8Array(size);
  window.crypto.getRandomValues(bytes);
  return bytes;
}

export function createZodiacCrushOperationId(namespace) {
  const safeNamespace = String(namespace || 'operation').replace(/[^A-Za-z0-9._:-]/g, '-');
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return `${OPERATION_ID_PREFIX}:${safeNamespace}:${window.crypto.randomUUID()}`;
  }
  const randomHex = Array.from(secureBytes(16), (value) => value.toString(16).padStart(2, '0')).join('');
  return `${OPERATION_ID_PREFIX}:${safeNamespace}:${randomHex}`;
}

export function clearZodiacCrushOperationIds() {
  ownerId = null;
  runOperationId = null;
  completionOperationIds.clear();
  missionEventIds.clear();
}

export function syncZodiacCrushOperationOwner(nextOwnerId) {
  const next = String(nextOwnerId || '').trim() || null;
  if (!next || (ownerId && ownerId !== next)) clearZodiacCrushOperationIds();
  ownerId = next;
  return ownerId;
}

function requireOwner(candidate) {
  const current = syncZodiacCrushOperationOwner(candidate);
  return current;
}

export function beginZodiacCrushRun(candidateOwnerId) {
  if (!requireOwner(candidateOwnerId)) return null;
  runOperationId = createZodiacCrushOperationId('run');
  completionOperationIds.clear();
  missionEventIds.clear();
  return runOperationId;
}

export function getZodiacCrushRunOperationId(candidateOwnerId) {
  if (!requireOwner(candidateOwnerId)) return null;
  if (!runOperationId) runOperationId = createZodiacCrushOperationId('run');
  return runOperationId;
}

export function getZodiacCrushCompletionOperationId(candidateOwnerId, logicalKey) {
  if (!requireOwner(candidateOwnerId)) return null;
  const key = String(logicalKey || '').trim();
  if (!key) throw new Error('completion_logical_key_required');
  if (!completionOperationIds.has(key)) {
    completionOperationIds.set(key, createZodiacCrushOperationId('completion'));
  }
  return completionOperationIds.get(key);
}

export function completeZodiacCrushCompletionOperation(candidateOwnerId, logicalKey) {
  if (!requireOwner(candidateOwnerId)) return;
  completionOperationIds.delete(String(logicalKey || '').trim());
}

export function getZodiacCrushMissionEventId(candidateOwnerId, logicalKey) {
  if (!requireOwner(candidateOwnerId)) return null;
  const key = String(logicalKey || '').trim();
  if (!key) throw new Error('mission_logical_key_required');
  if (!missionEventIds.has(key)) {
    missionEventIds.set(key, createZodiacCrushOperationId('mission'));
  }
  return missionEventIds.get(key);
}

export function completeZodiacCrushMissionEvent(candidateOwnerId, logicalKey) {
  if (!requireOwner(candidateOwnerId)) return;
  missionEventIds.delete(String(logicalKey || '').trim());
}

if (typeof window !== 'undefined') {
  const clearForLogout = () => clearZodiacCrushOperationIds();
  window.addEventListener('z10:authInvalidated', clearForLogout);
  window.addEventListener('z10:sessionInvalidated', clearForLogout);
  window.addEventListener('token:changed', (event) => {
    if (!event?.detail) clearForLogout();
  });
  window.addEventListener('storage', (event) => {
    if (event?.key === 'z10:auth_logout') clearForLogout();
  });
}

export function getZodiacCrushOperationIdentityForTests() {
  return {
    ownerId,
    runOperationId,
    completionCount: completionOperationIds.size,
    missionCount: missionEventIds.size,
  };
}
